import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useStore } from "react-redux";
import { PAGE_LOGIN } from "../../locales/pages/namespaces";
import useUserAction from "../../hooks/user/useUserAction";
import useUserData from "../../hooks/user/useUserData";
import * as actions from "../../actions/currentUser/actions";
import { useHistory, useLocation, Link as RouterLink } from 'react-router-dom'
import { ROUTE_PAGE_PRIVACY_POLICY, ROUTE_PAGE_TERMS_CONDITIONS } from "../../routers/routes";
import { getRouteUrl } from "../../helpers/getRouteUrl";
import { Grid, Link, Typography } from '@mui/material';
import { Capacitor } from "@capacitor/core";
import { FastLoginForm } from "../../components/fastLoginForm/fastLoginForm";
import * as yup from "yup";
import { useFormik } from "formik";
import { showPopup } from '../../components/globalPopup/globalPopupService';
import AuthBackground from "../../theme/mantis/assets/images/auth/authBackground";
import AuthCard from "../../theme/mantis/sections/auth/authCardLogin";
import loginimg from '../../assets/images/login-img.jpg';

const FastLogin = () => {
	const history = useHistory();
	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const urlKey = query.get('key') || '';
	const { t } = useTranslation(PAGE_LOGIN)
	const dispatch = useDispatch();
	const store = useStore();
	const { fastLoginCheckEmail, fastLoginVerifyOtp } = useUserAction();
	const {
		fastLoginEmailVerified,
		fastLoginOtpVerified,
		fastLoginLoading,
		fastLoginError
	} = useUserData();
	const [showOtp, setShowOtp] = useState(false);
	const [countdownTrigger, setCountdownTrigger] = useState(0);

	useEffect(() => {
		// Don't wipe the verified session when revisiting /fast-login —
		// the redirect effect below sends the user straight back to the application
		if (!fastLoginOtpVerified) {
			dispatch(actions.fastLoginReset())
		}
	}, [dispatch, fastLoginOtpVerified])

	useEffect(() => {
		document.title = t('IMS Financial Consulting | Login')
	}, [t]);

	useEffect(() => {
		// OTP input is only needed to verify a new code;
		// when data are already loaded (otpVerified) it stays hidden
		if (fastLoginEmailVerified && !fastLoginOtpVerified) {
			setShowOtp(true)
		} else if (fastLoginOtpVerified) {
			setShowOtp(false)
		}
	}, [fastLoginEmailVerified, fastLoginOtpVerified])

	useEffect(() => {
		if (fastLoginError) {
			showPopup({
				title: '',
				message: fastLoginEmailVerified ? t(fastLoginError) : t('Verification failed. Please contact your advisor.'),
				onConfirm: () => {}
			})
		}
	}, [fastLoginError, fastLoginEmailVerified, t])

	const validationSchema = yup.object({
		email: yup
			.string()
			.email(t('Invalid email'))
			.required(t('required')),
		otp: showOtp
			? yup.string()
				.matches(/^\S{6}$/, t('The code must be 6 characters'))
				.required(t('required'))
			: yup.string().notRequired()
	})

	const handleLogin = (values) => {
		// data already fetched — go straight into the loan, no call
		if (fastLoginOtpVerified) {
			history.push('/loans/application/fast-login')
			return
		}
		if (!fastLoginEmailVerified) {
			fastLoginCheckEmail(values.email, urlKey)
			return
		}
		// verify the otp with a call — redirect only on success, from this click
		const unsubscribe = store.subscribe(() => {
			const state = store.getState().currentUser
			if (state.get('fastLoginOtpVerified')) {
				unsubscribe()
				history.push('/loans/application/fast-login')
			} else if (state.get('fastLoginError')) {
				unsubscribe()
			}
		})
		fastLoginVerifyOtp(values.email, values.otp, urlKey)
	}

	const handleResendCode = () => {
		formik.setFieldValue('otp', '')
		formik.setFieldTouched('otp', false)
		setCountdownTrigger((prev) => prev + 1)
		fastLoginCheckEmail(formik.values.email, urlKey)
	}

	const formik = useFormik({
		initialValues: {
			email: '',
			otp: '',
		},
		validationSchema: validationSchema,
		onSubmit: handleLogin,
	})

	return (
		<Grid container className='mt-5'>
			<AuthBackground />
			<Grid container spacing={2}>
				<Grid item xs={12} sm={7} md={7}>
					<Grid sx={{
						display: {
							xs: 'none',
							sm: 'block',
						},
						backgroundImage: `url(${loginimg})`, backgroundRepeat: 'no-repeat',
						backgroundSize: 'cover',
						width: '100%',
						backgroundPosition: 'right',
						minHeight: '100%',
						height: {
							xs: '272px',
							sm: '100vh',
							md: '100vh',
						},
						boxShadow: {xs: 'inset 0px -25px 22px #ffffff', sm:'unset', md:'unset'}
					}}>
					</Grid>
				</Grid>
				<Grid item xs={12} sm={5} md={5} sx={{ display: 'flex', flexDirection: 'column' }}>
					<Grid sx={{ alignSelf: 'center', marginTop: 'auto', padding: '32px 24px 0 24px' }}>
						<AuthCard>
							<FastLoginForm
								sx={{marginBottom: '20px'}}
								formik={formik}
								loading={fastLoginLoading}
								showOtp={showOtp}
								otpVerified={fastLoginOtpVerified}
								countdownTrigger={countdownTrigger}
								onResendCode={handleResendCode}
							/>
						</AuthCard>
					</Grid>
					<Grid sx={{ display: 'flex', alignSelf: 'flex-end', marginTop: 'auto', zIndex: '10', width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', padding: '0 24px 32px 24px' }} >
					<Typography
						variant="h5"
						sx={{
							width: { xs: '100%', sm: '100%', md: 'initial' },
							textAlign: { xs: 'center', sm: 'center', md: 'left' },
							marginTop:'30px',
						}}
					>
						<Link
							component={RouterLink}
							className='color-default p-sm'
							to={getRouteUrl(ROUTE_PAGE_PRIVACY_POLICY)}
							target={!Capacitor.isNativePlatform() && "_blank"}
						>
							{t('Privacy policy')}
						</Link>
					</Typography>
					<Typography
						variant="h5"
						sx={{
							width: { xs: '100%', sm: '100%', md: 'initial' },
							textAlign: { xs: 'center', sm: 'center', md: 'right' },
							marginTop:'30px',
						}}
					>
						<Link
							component={RouterLink}
							className='color-default p-sm'
							to={getRouteUrl(ROUTE_PAGE_TERMS_CONDITIONS)}
							target={!Capacitor.isNativePlatform() && "_blank"}
						>
							{t('Terms & Conditions')}
						</Link>
					</Typography>
				</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default FastLogin
